import Dexie, { Table } from 'dexie';

export interface OfflineSale {
  id?: string;
  branchId: string;
  shiftId: string;
  items: Array<{
    productId: string;
    quantity: number;
    unitPrice: string;
    discountPercent?: string;
  }>;
  paymentMethod: 'CASH' | 'CARD' | 'MOBILE' | 'ACCOUNT';
  customerAccountId?: string;
  deliveryAddress?: string;
  subtotal: string;
  vat: string;
  total: string;
  status: 'queued' | 'synced' | 'failed';
  signature?: string;
  timestamp: number;
  createdAt: Date;
  syncedAt?: Date;
  errorMessage?: string;
}

export interface CachedProduct {
  id: string;
  name: string;
  category: string;
  price: string;
  active: boolean;
  searchText: string; // For fast search
}

export interface CachedInventory {
  productId: string;
  branchId: string;
  quantityOnHand: number;
  status: 'OK' | 'LOW' | 'OUT_OF_STOCK';
  updatedAt: Date;
}

export class MepcoDatabase extends Dexie {
  sales!: Table<OfflineSale>;
  products!: Table<CachedProduct>;
  inventory!: Table<CachedInventory>;

  constructor() {
    super('mepco_pos');
    this.version(1).stores({
      sales:
        '&id, status, branchId, shiftId, timestamp, [branchId+status]',
      products: '&id, searchText, category, active',
      inventory: '[productId+branchId], branchId, status',
    });
  }
}

export const db = new MepcoDatabase();

export async function initializeOfflineDB() {
  console.log('✅ Offline database initialized');
}

export async function queueSale(sale: OfflineSale) {
  sale.id = crypto.randomUUID();
  sale.timestamp = Date.now();
  sale.createdAt = new Date();
  sale.status = 'queued';
  return db.sales.add(sale);
}

export async function getQueuedSales(branchId: string) {
  return db.sales.where('[branchId+status]').equals([branchId, 'queued']).toArray();
}

export async function markSaleAsSynced(saleId: string) {
  return db.sales.update(saleId, { status: 'synced', syncedAt: new Date() });
}

export async function markSaleAsFailed(saleId: string, error: string) {
  return db.sales.update(saleId, { status: 'failed', errorMessage: error });
}

export async function cacheProducts(products: CachedProduct[]) {
  const productsWithSearch = products.map(p => ({
    ...p,
    searchText: `${p.name} ${p.category}`.toLowerCase(),
  }));
  return db.products.bulkPut(productsWithSearch);
}

export async function searchProducts(query: string) {
  const lowerQuery = query.toLowerCase();
  return db.products
    .where('searchText')
    .startsWithIgnoreCase(lowerQuery)
    .toArray();
}

export async function cacheInventory(inventory: CachedInventory[]) {
  return db.inventory.bulkPut(inventory);
}

export async function getInventoryForBranch(branchId: string) {
  return db.inventory.where('branchId').equals(branchId).toArray();
}
